import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardTitle,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonImg,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonRadio,
  IonRadioGroup,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { calendarOutline, mapOutline, peopleOutline } from "ionicons/icons";
import { FormattedDate, FormattedTime } from "react-intl";
import useAPI from "../../hooks/basic/useAPI";

import "./styles/SwitchLessonModal.scss";

// Firestore Timestamps arrive as `.seconds` from the client SDK and `._seconds`
// once serialized through the functions API — accept either.
const tsToMillis = (ts) => (ts?.seconds ?? ts?._seconds ?? 0) * 1000;

// A lesson rendered as a card (same visual language as BookingCard) so the user
// can see WHO will attend it. Clicking selects it; the selected card is
// highlighted. No navigation — it only drives the selection.
function SwitchTargetCard({ target, selected, onSelect }) {
  const participants = target.participants || [];
  return (
    <IonCard
      className={`SwitchTargetCard${selected ? " selected" : ""}`}
      style={{ display: "flex", flexDirection: "row" }}
      data-target-id={target.id}
      onClick={() => onSelect(target.id)}
    >
      <IonImg
        src="smash.png"
        style={{ width: "55px", paddingLeft: "1rem", boxSizing: "content-box" }}
      />
      <IonCardContent style={{ flex: 1, paddingLeft: "1rem" }}>
        <IonText>
          <h5>
            <IonIcon icon={calendarOutline}></IonIcon>
            <FormattedDate
              value={tsToMillis(target.from)}
              day="2-digit"
              month="long"
              weekday="short"
            />
            &nbsp;-&nbsp;
            <FormattedTime value={tsToMillis(target.from)} />
          </h5>
        </IonText>
        <IonCardTitle>
          {target.label || target.description || "Prenotazione"}
        </IonCardTitle>
        {(target.clubName || target.fieldName) && (
          <IonText>
            <h5>
              <IonIcon icon={mapOutline}></IonIcon>
              {[target.clubName, target.fieldName].filter(Boolean).join(" - ")}
            </h5>
          </IonText>
        )}
        <IonText>
          <h5>
            <IonIcon icon={peopleOutline}></IonIcon>
            {participants.length
              ? participants.map((p) => p.fullName).join(", ")
              : "Nessun partecipante"}
            {typeof target.remainingSlots === "number" &&
              ` · ${target.remainingSlots} posti liberi`}
          </h5>
        </IonText>
      </IonCardContent>
    </IonCard>
  );
}

SwitchTargetCard.propTypes = {
  target: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

// Two directions, same atomic swap endpoint:
//  - "out": from the lesson the user is enrolled in (`booking`), pick a future
//    destination to move to.
//  - "in":  from a bookable lesson (`booking`), pick one of the user's existing
//    enrollments to give up and take this one instead. Customer-only.
function SwitchLessonModal({
  booking,
  appUserId,
  isManager,
  mode = "out",
  onDismiss,
  onSwitched,
}) {
  const { callAPI } = useAPI();
  const participants = booking.participants || [];
  const isIn = mode === "in";

  // "out" lets a manager pick which participant to move; "in" is always the
  // current user moving one of their own enrollments.
  const [selectedUserId, setSelectedUserId] = useState(
    isIn || !isManager ? appUserId : null,
  );
  const [candidates, setCandidates] = useState(null);
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!selectedUserId) return undefined;
    let active = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      setCandidates(null);
      setExpired(false);
      setSelectedId(null);
      try {
        const response = await callAPI({
          url: isIn ? "/switch-sources" : "/switch-targets",
          params: isIn
            ? { userId: selectedUserId, toLessonId: booking.id }
            : { userId: selectedUserId, fromLessonId: booking.id },
        });
        const list = Array.isArray(response)
          ? response
          : (isIn ? response?.sources : response?.targets) || [];
        if (active) {
          setCandidates(list);
          setExpired(response?.expired === true);
        }
      } catch (err) {
        if (active) {
          setError("Errore nel caricamento delle lezioni disponibili");
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [selectedUserId]);

  const confirm = async () => {
    if (!selectedUserId || !selectedId) return;
    setSubmitting(true);
    setError(null);
    try {
      await callAPI({
        method: "post",
        url: "/switch-lesson",
        data: {
          userId: selectedUserId,
          // "in": the bookable lesson is the destination, the picked card the
          // source. "out": the opposite.
          fromLessonId: isIn ? selectedId : booking.id,
          toLessonId: isIn ? booking.id : selectedId,
        },
      });
      onSwitched();
    } catch (err) {
      setError(err?.response?.data?.error || "Errore durante lo spostamento");
      setSubmitting(false);
    }
  };

  const emptyMessage = isIn
    ? "Non hai lezioni dello stesso corso da scambiare."
    : "Nessuna lezione futura disponibile nello stesso corso.";
  const prompt = isIn
    ? "Scegli quale tua lezione lasciare per iscriverti a questa:"
    : "Scegli la lezione di destinazione:";

  return (
    <IonModal isOpen onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{isIn ? "Scambia lezione" : "Sposta lezione"}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss} disabled={submitting}>
              Chiudi
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {!isIn && isManager && (
          <>
            <IonText>
              <p>Scegli il partecipante da spostare:</p>
            </IonText>
            <IonRadioGroup
              value={selectedUserId}
              onIonChange={(e) => setSelectedUserId(e.detail.value)}
            >
              <IonList>
                {participants.map((p) => (
                  <IonItem key={p.userId || p.id}>
                    <IonLabel>{p.fullName || p.userId}</IonLabel>
                    <IonRadio slot="end" value={p.userId} />
                  </IonItem>
                ))}
              </IonList>
            </IonRadioGroup>
          </>
        )}

        {selectedUserId && (
          <>
            <IonText>
              <p>{prompt}</p>
            </IonText>
            {loading && <IonSpinner />}
            {!loading && expired && (
              <IonText color="danger">
                <p>
                  Il diritto al recupero è scaduto: non è più possibile spostare
                  questa lezione.
                </p>
              </IonText>
            )}
            {!loading && !expired && candidates && candidates.length === 0 && (
              <IonText color="medium">
                <p>{emptyMessage}</p>
              </IonText>
            )}
            {!loading &&
              candidates &&
              candidates.length > 0 &&
              candidates.map((c) => (
                <SwitchTargetCard
                  key={c.id}
                  target={c}
                  selected={selectedId === c.id}
                  onSelect={setSelectedId}
                />
              ))}
          </>
        )}

        {error && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}
      </IonContent>
      <IonFooter
        className="ion-no-border ion-padding"
        style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}
      >
        <IonButton
          expand="block"
          size="large"
          color="success"
          disabled={!selectedId || submitting}
          onClick={confirm}
        >
          {submitting ? <IonSpinner /> : isIn ? "Scambia" : "Sposta"}
        </IonButton>
        <IonButton
          expand="block"
          size="large"
          fill="clear"
          onClick={onDismiss}
          disabled={submitting}
        >
          Annulla
        </IonButton>
      </IonFooter>
    </IonModal>
  );
}

SwitchLessonModal.propTypes = {
  booking: PropTypes.shape({
    id: PropTypes.string,
    participants: PropTypes.array,
  }).isRequired,
  appUserId: PropTypes.string,
  isManager: PropTypes.bool,
  mode: PropTypes.oneOf(["out", "in"]),
  onDismiss: PropTypes.func.isRequired,
  onSwitched: PropTypes.func.isRequired,
};

export default SwitchLessonModal;
